import { memo, useCallback, useEffect, useState } from "react";

// Helpers
import { useTranslate } from "helpers";

// Servisni sloj
import { getDamageStatusesAPI } from "services/api/fleet/vehicles/damages/api";
import { initialFleetVehicleDamageSearchForm } from "services/api/fleet/vehicles/damages/consts";
import { DamageStatus, FleetVehicleDamageSearchFormValues } from "services/api/fleet/vehicles/damages/types";

// UI
import { DatePicker } from "ui/components/DatePicker";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { TextField } from "ui/components/TextField/TextField";
import { DAMAGE_STATUS_HEX, DEFAULT_DAMAGE_STATUS_HEX } from "ui/modules/fleet/vehicles/pages/VehicleDetailPage/components/VehicleDetailCard/components/VehicleDamageEditCard/components/consts";

import { VehicleDamageSearchFilterProps } from "./types";

/**
 * Filteri šteta jednog vozila: isti kao u pregledu svih šteta (DamageSearchFilters), bez marke,
 * modela i tipa, jer je vozilo već zadano.
 */
const VehicleDamageSearchFilters: React.FC<VehicleDamageSearchFilterProps> = (props) => {
  const { onSearchChange } = props;
  const { t } = useTranslate();

  const [formValues, setFormValues] = useState<FleetVehicleDamageSearchFormValues>(
    initialFleetVehicleDamageSearchForm
  );
  const [statuses, setStatuses] = useState<DamageStatus[]>([]);

  useEffect(() => {
    fetchStatuses();
  }, []);

  const fetchStatuses = async () => {
    try {
      setStatuses(await getDamageStatusesAPI());
    } catch {
      setStatuses([]);
    }
  };

  const updateFormValues = useCallback(
    (updatedFormValues: FleetVehicleDamageSearchFormValues) => {
      setFormValues(updatedFormValues);
      onSearchChange(updatedFormValues);
    },
    [onSearchChange]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      updateFormValues({ ...formValues, search: e.target.value });
    },
    [formValues, updateFormValues]
  );

  const handleStatusChange = useCallback(
    (_e: any, _name: string, item: DropdownOption) => {
      updateFormValues({ ...formValues, vehicleDamageStatusId: item.value || null });
    },
    [formValues, updateFormValues]
  );

  const handleDateChange = useCallback(
    (name: string, value: Date | null) => {
      updateFormValues({ ...formValues, [name]: value });
    },
    [formValues, updateFormValues]
  );

  const handleResetFormClick = useCallback(() => {
    updateFormValues(initialFleetVehicleDamageSearchForm);
  }, [updateFormValues]);

  // Boja statusa ista je kao kod unosa štete i pinova na fotografiji.
  const statusItems: DropdownOption[] = statuses.map((status) => ({
    value: status.id,
    text: status.name,
    color: DAMAGE_STATUS_HEX[status.color ?? ""] ?? DEFAULT_DAMAGE_STATUS_HEX,
  }));

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="min-w-[220px]">
        <TextField
          type="text"
          name="search"
          value={formValues.search}
          onChange={handleSearchChange}
          placeholder="Naziv štete..."
          startIcon="/media/icons/search.svg"
          cssClassName="bg-white"
        />
      </div>
      <div className="min-w-[180px]">
        <Dropdown
          name="vehicleDamageStatusId"
          placeholder="Status"
          options={statusItems}
          value={formValues.vehicleDamageStatusId}
          onChange={handleStatusChange}
          cssClassName="bg-white"
          width="full"
        />
      </div>
      <div className="w-[150px]">
        <DatePicker
          name="damageDateFrom"
          label="Datum štete od - do"
          value={formValues.damageDateFrom}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="w-[150px]">
        <DatePicker
          name="damageDateTo"
          value={formValues.damageDateTo}
          onChange={handleDateChange}
          cssClassName="bg-white"
        />
      </div>
      <div className="flex items-center pb-2">
        <span
          onClick={handleResetFormClick}
          className="text-xs font-bold cursor-pointer text-gray-700"
        >
          {t("admin.reset")}
        </span>
      </div>
    </div>
  );
};

export default memo(VehicleDamageSearchFilters);
